'use client';

import { useEffect } from 'react';

import {
  usePathname,
  useRouter,
  useSelectedLayoutSegment,
} from 'next/navigation';

import { legacyLinkTargetToPath } from '../functions/legacyLinkTarget';
import {
  CONTENT_TABS_ANCHOR_PATH,
  linkNodeHash,
  linkNodePathFromHash,
} from '../functions/linkNodePath';
import { takePendingLinkJump } from '../functions/pendingLinkJump';
import { scrollToLinkNode } from '../functions/scrollToLinkNode';

/** How long to wait for a section on the loaded tab before falling back to its deep page. */
const FALLBACK_WAIT_MS = 2500;

/** Child-route segments of `(profile)` that are content tabs (vs. the bare index). */
const CONTENT_TAB_SEGMENTS = new Set(['store', 'life', 'feed']);

/**
 * Whether this document has already handled its initial load. Module-scoped so it
 * survives layout remounts (e.g. back-nav from a detail page) — the implicit
 * cold-load jump must fire ONCE per hard page load, never on subsequent soft
 * navigation. A refresh is a new document, so the flag resets and it fires again.
 */
let didHandleInitialLoad = false;

interface Jump {
  anchorPath: string;
  fallbackHref?: string;
  /** The target rode in a pending jump (clean nav) → reflect the shareable `#…`. */
  reflectFragment: boolean;
}

/**
 * Visitor-side automatic jump: scrolls the bio to a section on tab arrival.
 *
 * The target normally comes from a ONE-SHOT pending jump the link bar stashed on
 * click (see {@link takePendingLinkJump}): the shortcut navigated CLEAN (no `#…`
 * in the router state), so on arrival we scroll and then reflect the SHAREABLE
 * fragment in the address bar via the History API. As a fallback for shared /
 * cold / legacy deep links it reads the URL `#<path>` fragment (already shareable)
 * or the legacy `?t=<target>` query param. Re-runs on `hashchange` and on every
 * content-tab navigation (`usePathname`), since the bio's tabs are routes and the
 * shell layout — which mounts this hook — persists across them.
 *
 * A section-anchor collection/series whose target sits on an unloaded
 * infinite-scroll page (so the scroll can't find it) navigates to the item's deep
 * page instead. That fallback is ONE-SHOT, so it fires only for the click that
 * requested it — never when the visitor later lands on the same URL again.
 *
 * IMPLICIT jump: on the initial COLD/HARD load of a content-tab route
 * (`/store` · `/life` · `/feed`) with NO explicit target (no pending jump, no
 * `#…`, no `?t=`), it jumps to the content section so the visitor lands on the
 * content rather than the top of the profile. Fires once per document; a shared
 * `#…` deep link always wins over it.
 *
 * Stateless, no context, decoupled from the my-popstore authoring system.
 */
export function useBioHashScroll(): void {
  const pathname = usePathname();
  const router = useRouter();
  const segment = useSelectedLayoutSegment();

  useEffect(() => {
    const resolveJump = (): Jump | undefined => {
      const pending = takePendingLinkJump();
      if (pending != null) {
        return { ...pending, reflectFragment: true };
      }
      const fromHash = linkNodePathFromHash(window.location.hash);
      if (fromHash != null) {
        return { anchorPath: fromHash, reflectFragment: false };
      }
      const legacy = new URLSearchParams(window.location.search).get('t');
      const fromLegacy =
        legacy != null ? legacyLinkTargetToPath(legacy) : undefined;
      return fromLegacy != null
        ? { anchorPath: fromLegacy, reflectFragment: false }
        : undefined;
    };

    let cancel: (() => void) | undefined;
    const run = () => {
      const jump = resolveJump();
      if (jump == null) {
        // No explicit target. Only on the initial cold/hard load of a content-tab
        // route, implicitly jump to the content section (past the profile header).
        if (!didHandleInitialLoad) {
          didHandleInitialLoad = true;
          if (segment != null && CONTENT_TAB_SEGMENTS.has(segment)) {
            cancel?.();
            cancel = scrollToLinkNode(CONTENT_TABS_ANCHOR_PATH);
          }
        }
        return;
      }
      didHandleInitialLoad = true;
      const { anchorPath, fallbackHref, reflectFragment } = jump;
      cancel?.();
      cancel = scrollToLinkNode(anchorPath, {
        timeoutMs: fallbackHref != null ? FALLBACK_WAIT_MS : undefined,
        onTimeout:
          fallbackHref != null ? () => router.replace(fallbackHref) : undefined,
      });
      if (reflectFragment) {
        // Clean push landed us on the bare tab route; put the shareable fragment
        // back on the address bar via the History API only — Next's router state
        // stays fragment-free, so a later tab click clears cleanly.
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${window.location.search}${linkNodeHash(anchorPath)}`,
        );
      }
    };

    run();
    window.addEventListener('hashchange', run);
    return () => {
      window.removeEventListener('hashchange', run);
      cancel?.();
    };
  }, [pathname, router, segment]);
}
